import { useJobChainDag, useJobChainDetails } from '@/api/Jobs';
import { JobDetail } from '@/modules/Jobs/components/JobDetails';
import { JobData, JobStatus } from '@/types';
import { Alert, Button, Empty, Space, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { JobChainDashboard } from './JobChainDashboard.tsx';

interface JobChainDetailProps {
  jobChainId: number;
  jobStatus: JobStatus[];
}

export const JobChainDetail: FC<JobChainDetailProps> = ({
  jobChainId,
  jobStatus,
}) => {
  const {
    data: jobChainDetail,
    isLoading: isJobChainDetailLoading,
    isError: isJobChainDetailError,
    refetch: refetchJobChainDetail,
  } = useJobChainDetails(jobChainId);
  const {
    data: jobChainDag,
    isLoading: isJobChainDagLoading,
    isError: isJobChainDagError,
    refetch: refetchJobChainDag,
  } = useJobChainDag(jobChainId);
  const isLoading = isJobChainDetailLoading || isJobChainDagLoading;

  const jobs = useMemo(() => {
    const list = jobChainDetail?.jobs ?? [];
    return [...list].sort((a, b) => a.orderSequence - b.orderSequence);
  }, [jobChainDetail?.jobs]);

  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  useEffect(() => {
    if (!jobs.length) {
      setSelectedJobId(null);
      return;
    }
    setSelectedJobId((prev) => {
      if (prev != null && jobs.some((j: JobData) => j.jobId === prev)) {
        return prev;
      }
      return jobs[0].jobId;
    });
  }, [jobs]);

  const selectedJob = useMemo(
    () => jobs.find((j) => j.jobId === selectedJobId),
    [jobs, selectedJobId],
  );

  return (
    <Spin spinning={isLoading}>
      <Space direction="vertical" size="middle" className="w-100">
        {isJobChainDetailError && (
          <Alert
            type="error"
            showIcon
            message="Job Chain details could not be loaded."
            action={
              <Button onClick={() => void refetchJobChainDetail()}>
                Retry details
              </Button>
            }
          />
        )}
        {isJobChainDagError ? (
          <Alert
            type="error"
            showIcon
            message="Job Chain DAG could not be loaded."
            action={
              <Button onClick={() => void refetchJobChainDag()}>
                Retry DAG
              </Button>
            }
          />
        ) : (
          <JobChainDashboard
            jobChainDag={jobChainDag}
            onClickJob={(jobId) =>
              setSelectedJobId(selectedJobId == jobId ? null : jobId)
            }
          />
        )}
        {!isJobChainDetailError &&
          (selectedJob ? (
            <JobDetail job={selectedJob} jobStatus={jobStatus} />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Click on a job to view details"
            />
          ))}
      </Space>
    </Spin>
  );
};
